import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { employeeAPI, alertsAPI } from '../services/api';
import { FEELINGS, ALINEA_PROFILES, BOSS_PERCEPTION, TEAM_FEELING } from '../utils/constants';

function mapEmployeeApiToDetalle(raw) {
    if (!raw) return null;
    const daily = raw.dailySurveys || [];
    const starts = daily.filter((d) => d.survey_type === 'start' || d.survey_type == null);
    const checkins = starts.map((c) => ({
        estado_animo: c.feeling,
        fecha: c.survey_date,
    }));
    const weekly = [...(raw.weeklySurveys || [])].sort((a, b) =>
        String(b.week_start).localeCompare(String(a.week_start))
    );
    const w0 = weekly[0];
    return {
        perfil: {
            nombre_completo: raw.full_name,
            area_nombre: raw.area_name,
            puesto: raw.position || '—',
            perfil_alinea: raw.alinea_profile || 'constante',
        },
        checkins,
        ultimaEncuesta: w0
            ? {
                  relacion_jefe: w0.boss_perception,
                  clima_equipo: w0.team_feeling,
              }
            : null,
        alertas: (raw.alerts || [])
            .filter((a) => a.status === 'active')
            .map((a) => ({ id: a.id, nivel: a.level, codigo_regla: a.alert_code })),
    };
}

/** Seguimiento en columna 3 — EPA visible al elegir persona. */
export default function SupervisorWorkerPanel({ workerId, workerRow, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notes, setNotes] = useState('');
    const [intervention, setIntervention] = useState('');

    useEffect(() => {
        if (!workerId) return undefined;
        setLoading(true);
        setData(null);
        setIntervention('');
        const saved = localStorage.getItem(`notes_worker_${workerId}`);
        setNotes(saved || '');
        employeeAPI
            .getDetalle(workerId)
            .then((res) => {
                const payload = res.data?.data ?? res.data;
                setData(mapEmployeeApiToDetalle(payload));
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [workerId]);

    function saveNotes(val) {
        setNotes(val);
        localStorage.setItem(`notes_worker_${workerId}`, val);
    }

    async function handleIntervention() {
        if (!intervention.trim()) return;
        const alertId = data?.alertas?.[0]?.id;
        if (alertId) {
            await alertsAPI.intervene(alertId, { actionTaken: intervention, outcome: null });
        }
        setIntervention('');
        const res = await employeeAPI.getDetalle(workerId);
        setData(mapEmployeeApiToDetalle(res.data?.data ?? res.data));
    }

    if (!workerId) return null;

    const mood = workerRow?.estado_hoy;
    const moodOpt = FEELINGS[mood];

    if (loading) {
        return <p className="sup-panel-loading">Cargando contexto…</p>;
    }

    if (!data) {
        return (
            <div className="sup-panel">
                <p className="sup-panel-error">No se pudo cargar el detalle.</p>
                <Link to={`/team/${workerId}`} className="sup-link-btn">
                    Ver ficha completa →
                </Link>
            </div>
        );
    }

    const { perfil, checkins, ultimaEncuesta, alertas } = data;
    const profileInfo = ALINEA_PROFILES[perfil.perfil_alinea?.toLowerCase()] || ALINEA_PROFILES.constante;

    return (
        <div className="sup-panel">
            <div className="sup-panel-head">
                <button type="button" className="sup-panel-close" onClick={onClose} aria-label="Cerrar">
                    ✕
                </button>
                <h3 className="sup-panel-name">{perfil.nombre_completo}</h3>
                <p className="sup-panel-meta">
                    {perfil.area_nombre} · {perfil.puesto}
                </p>
            </div>

            <div className="sup-panel-mood-row">
                <span className="sup-panel-mood-emoji" aria-hidden>
                    {moodOpt?.emoji || FEELINGS[checkins[0]?.estado_animo]?.emoji || '·'}
                </span>
                <div>
                    <p className="sup-panel-mood-label">Estado actual</p>
                    <p className="sup-panel-mood-val">{moodOpt?.label || 'Sin registro reciente'}</p>
                    <p className="sup-panel-mood-note">Última llegada a jornada</p>
                </div>
                <span className="sup-panel-profile-chip">
                    {profileInfo.emoji} {profileInfo.name}
                </span>
            </div>

            {ultimaEncuesta && (
                <div className="sup-panel-weekly">
                    <p className="sup-panel-section-label">Encuesta semanal</p>
                    <p className="sup-panel-line">
                        {BOSS_PERCEPTION[ultimaEncuesta.relacion_jefe]?.emoji}{' '}
                        {BOSS_PERCEPTION[ultimaEncuesta.relacion_jefe]?.label || '—'}
                    </p>
                    <p className="sup-panel-line">
                        {TEAM_FEELING[ultimaEncuesta.clima_equipo]?.emoji}{' '}
                        {TEAM_FEELING[ultimaEncuesta.clima_equipo]?.label || '—'}
                    </p>
                </div>
            )}

            {alertas.length > 0 && (
                <p className="sup-panel-alert-tag">
                    {alertas.length} alerta{alertas.length > 1 ? 's' : ''} activa
                    {alertas.length > 1 ? 's' : ''} — revísalas en la pestaña Alertas
                </p>
            )}

            <textarea
                className="sup-panel-notes"
                rows={2}
                value={notes}
                onChange={(e) => saveNotes(e.target.value)}
                placeholder="Notas privadas (solo tú)…"
            />

            <div className="sup-panel-epa-open">
                <p className="sup-panel-section-label">Intervención EPA</p>
                <p className="sup-panel-hint">Escucha · Procesa · Ajusta — ¿Qué acordaron?</p>
                <textarea
                    className="sup-panel-notes"
                    rows={3}
                    value={intervention}
                    onChange={(e) => setIntervention(e.target.value)}
                    placeholder="Ej: pausa breve y revisión de carga…"
                />
                <button type="button" className="sup-alert-action" onClick={handleIntervention}>
                    Guardar intervención
                </button>
            </div>

            <Link to={`/team/${workerId}`} className="sup-link-btn">
                Ver ficha completa →
            </Link>
        </div>
    );
}
